<template>
  <div>
    <div class="mb-4">
      <h6 class="text-subtitle-1 text-md-h6 font-weight-bold mb-1">
        Servicios a facturar
      </h6>
      <p
        class="mb-0 text-subtitle-2 text-md-subtitle-1 font-weight-regular grey--text text--darken-1"
      >
        Seleccioná los servicios que quieras incluir en esta factura. Podés
        agregar hasta 15 por factura.
      </p>
    </div>
    <SkeletonForm v-if="isLoading" />
    <main
      v-else-if="!isLoading && pendingTransactions"
      class="d-flex flex-column"
    >
      <v-checkbox
        v-model="selectAll"
        color="blue darken-2"
        style="width: fit-content"
        class="pl-3 pl-md-6 mt-0"
        label="Seleccionar todos"
        :disabled="pendingTransactions.every((t) => t.blocked_by_issue)"
        :indeterminate="pendingTransactions.every((t) => t.blocked_by_issue)"
        @change="toggleSelectedAll"
      ></v-checkbox>
      <section class="d-flex flex-column mt-0 mb-5" style="gap: 0.5rem">
        <v-card
          v-for="transaction of pendingTransactions"
          :key="transaction.id"
          style="border-left: 8px solid #ff9800"
        >
          <v-row no-gutters class="d-flex align-center mr-1 mr-md-0">
            <v-col cols="8" class="pl-1 pr-0 pr-md-3 d-flex align-center">
              <v-checkbox
                :disabled="
                  disableCheckbox(transaction) || transaction.blocked_by_issue
                "
                :indeterminate="
                  disableCheckbox(transaction) || transaction.blocked_by_issue
                "
                :value="transaction"
                v-model="formData.transactions"
                class="ml-3 ml-md-6"
                color="blue darken-2"
                @change="v.transactions.$touch()"
              ></v-checkbox>
              <v-card-text class="d-flex flex-column px-0">
                <span
                  :style="{
                    lineHeight: $vuetify.breakpoint.mdAndUp ? '1.4' : '1.3',
                  }"
                  class="text-subtitle-1 text-md-h6 font-weight-medium"
                  >{{ transaction.service }}</span
                >
                <span
                  class="text-subtitle-2 grey--text text--darken-1 font-weight-regular"
                  >Pedido {{ transaction.request?.id }}</span
                >
              </v-card-text>
            </v-col>
            <v-col cols="4" sm="4" md="4" class="px-3">
              <div class="d-flex flex-column text-end">
                <h6
                  class="text-subtitle-1 text-md-h6 font-weight-medium grey--text text--darken-1 short-text"
                >
                  {{ formatCurrency(transaction.amount) }}
                  <span v-show="ivaCondition === RESP_INSCRIPTO">+IVA</span>
                </h6>
                <div v-if="transaction.paid" class="d-flex justify-end">
                  <v-btn
                    class="px-0 text-none"
                    text
                    color="green"
                    @click="openSuccesDialog()"
                  >
                    <v-icon small> mdi-alert-circle-outline </v-icon>
                    <span>Cobrado el {{ transaction.paid }}</span>
                  </v-btn>
                </div>

                <div
                  v-else-if="transaction.blocked_by_issue"
                  class="d-flex justify-end"
                >
                  <v-hover v-slot="{ hover }">
                    <v-btn
                      text
                      elevation="0"
                      class="px-0 text-none"
                      :color="hover ? 'blue darken-2' : ''"
                      style="opacity: 0.6; display: flex; justify-content: end"
                      @click="showAlert(transaction)"
                    >
                      <v-icon size="20">mdi-information-outline</v-icon>
                      <span class="hidden-sm-and-down ml-1"
                        >Ver información</span
                      >
                    </v-btn>
                  </v-hover>
                </div>

                <template
                  v-if="transactionRequestHasGruaSubScheme(transaction)"
                >
                  <div
                    v-if="
                      transaction.status === 6 && !transaction.blocked_by_issue
                    "
                    class="d-flex justify-end"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-btn
                        text
                        elevation="0"
                        class="px-0 text-none"
                        :color="hover ? 'blue darken-2' : ''"
                        style="
                          opacity: 0.6;
                          display: flex;
                          justify-content: end;
                        "
                        @click="openRevisionDialog(transaction)"
                      >
                        <v-icon size="20"> mdi-file-search-outline </v-icon>
                        <span class="hidden-sm-and-down ml-1"
                          >Solicitar revisión</span
                        >
                      </v-btn>
                    </v-hover>
                  </div>
                  <div
                    v-if="transaction.status === PENDING_CONCILIATION"
                    class="d-flex justify-end"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-btn
                        text
                        elevation="0"
                        class="px-0 text-none"
                        :color="hover ? 'blue darken-2' : ''"
                        style="
                          opacity: 0.6;
                          display: flex;
                          justify-content: end;
                        "
                        @click="openRevisionDetailDialog(transaction)"
                      >
                        <v-icon size="20"> mdi-information-outline </v-icon>
                        <span class="hidden-sm-and-down ml-1"
                          >Revisión solicitada</span
                        >
                      </v-btn>
                    </v-hover>
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <p
        v-if="v.transactions.$error"
        class="subtitle-1 red--text darken-4 font-weight-medium"
      >
        {{ transactionErrors[0] }}
      </p>

      <v-alert
        v-model="transactionLimitReached"
        color="orange darken-1"
        dense
        icon="mdi-alert-outline"
        outlined
        text
      >
        Podés seleccionar hasta {{ MAX_TRANSACTIONS }} servicios por factura
      </v-alert>

      <div class="pl-5 pl-md-15 pr-2 pr-md-8">
        <div
          class="text-uppercase text-subtitle-1 text-md-h6 font-weight-medium d-flex justify-space-between mb-2"
        >
          <span>total a facturar:</span>
          <div style="display: flex; gap: 0.5rem">
            <span class="grey--text text--darken-1">{{
              formatCurrency(totalAmountAll)
            }}</span>
            <span v-show="ivaCondition === RESP_INSCRIPTO && totalAmountAll > 0"
              >+IVA</span
            >
          </div>
        </div>
        <div
          class="text-uppercase text-subtitle-1 text-md-h6 font-weight-medium green--text d-flex justify-space-between mb-5"
        >
          <span>total a cobrar:</span>
          <div style="display: flex; gap: 0.5rem">
            <span>{{ formatCurrency(totalAmountWithoutPaid) }}</span>
            <span
              v-show="
                ivaCondition === RESP_INSCRIPTO && totalAmountWithoutPaid > 0
              "
              >+IVA</span
            >
          </div>
        </div>
      </div>
    </main>

    <v-dialog
      v-model="warningAlert"
      v-if="warningAlert && selectedTransaction"
      max-width="641"
    >
      <v-card>
        <v-card-title class="pa-4 pt-6 pb-0 d-flex align-start">
          <v-icon color="blue darken-2" size="48" class="mr-4 revision-icon"
            >mdi-alert-circle-outline</v-icon
          >
          <span class="font-weight-bold hidden-sm-and-down"
            >Pedido con reclamo</span
          >
          <v-spacer></v-spacer>
          <v-icon class="revision-icon" @click="closeAlert">clear</v-icon>
          <span
            class="font-weight-bold hidden-sm-and-up pt-4 pb-2"
            style="width: 100%"
            >Pedido con reclamo</span
          >
        </v-card-title>
        <v-card-text class="revision-text-container">
          <span style="opacity: 0.9">
            Por el momento no podés facturar este servicio ya que el pedido
            tiene un reclamo de garantía en curso. No te preocupes, podrás
            facturarlo cuando se resuelva el reclamo.
          </span>
        </v-card-text>
        <v-card-actions class="pa-6 pt-8">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="blue darken-2 white--text"
            class="px-5 font-weight-bold wid"
            large
            @click="closeAlert"
          >
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="revisionDialog" max-width="714" persistent>
      <v-card>
        <v-form ref="revisionFormRef" v-model="formValid">
          <v-card-title class="pt-6 px-6 pb-0 d-flex align-start">
            <v-icon size="48" class="mr-4 revision-icon" color="orange darken-1"
              >mdi-file-search-outline</v-icon
            >
            <span class="font-weight-bold hidden-sm-and-down"
              >Solicitar revisión del servicio</span
            >
            <v-spacer></v-spacer>
            <v-icon class="revision-icon" @click="revisionDialog = false"
              >clear</v-icon
            >
            <span class="font-weight-bold hidden-sm-and-up pt-4 pb-2"
              >Solicitar revisión del servicio</span
            >
          </v-card-title>
          <v-card-text class="revision-text-container">
            <span style="opacity: 0.9">
              ¿Notaste un error en el monto? Podés pedir una revisión del
              servicio para que nuestro equipo de administración lo verifique.
            </span>
            <div
              v-if="
                transactionToRevision?.request?.sub_scheme ===
                  'Mecanica ligera' ||
                transactionToRevision?.request?.sub_scheme === 'Gruas'
              "
            >
              <span class="font-weight-medium pb-2"> Datos del pedido: </span>
              <v-list dense class="pb-0">
                <v-list-item
                  v-for="(item, index) in requestDetails"
                  :key="index"
                  class="pl-0"
                >
                  <v-list-item-content class="pb-1 pt-0 request-information">
                    <div>
                      <span class="mr-2">{{ item.icon }}</span>
                      <span class="font-weight-medium">{{ item.label }}:</span>
                      {{ item.text }}
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <div>
              <span class="text-body-2" style="opacity: 0.6; font-style: italic"
                >Si necesitás más información, podés ver el detalle completo del
                pedido:</span
              >
              <v-btn
                text
                class="text-none blue--text text--darken-2 pa-0 text-decoration-none text-body-1 font-weight-bold"
                @click="showRequest(transactionToRevision.request.id)"
                >Ver pedido completo</v-btn
              >
            </div>
            <v-row>
              <v-col cols="12" class="d-flex flex-column" style="gap: 16px">
                <v-textarea
                  v-model="revisionForm.reasonReview"
                  :rules="[rules.required, rules.max500]"
                  outlined
                  rows="3"
                  label="Motivo de revisión"
                  color="blue darken-2"
                  no-resize
                  hide-details="auto"
                ></v-textarea>
                <v-text-field
                  v-model="revisionForm.suggestedAmount"
                  :rules="[rules.required, rules.numeric]"
                  outlined
                  label="Monto sugerido"
                  color="blue darken-2"
                  prefix="$"
                  class="custom-prefix"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions
            class="pa-6 pt-6 d-flex flex-column-reverse flex-sm-row"
          >
            <v-spacer class="hidden-xs-only"></v-spacer>
            <v-btn
              text
              @click="cancelRevision"
              :disabled="requestRevisionIsLoading"
              style="opacity: 0.8"
              large
              class="font-weight-bold px-4 mb-2 mb-sm-0 wid"
              >Cancelar</v-btn
            >
            <v-btn
              depressed
              color="blue darken-2 white--text"
              :disabled="!formValid"
              @click="submitRevisionForm"
              large
              class="font-weight-bold px-4 d-block wid"
              :loading="requestRevisionIsLoading"
              >Solicitar revisión</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="successDialog" max-width="542">
      <v-card>
        <v-card-title class="pt-6 px-6 pb-0 d-flex align-start">
          <v-spacer></v-spacer>
          <v-icon @click="successDialog = false">clear</v-icon>
        </v-card-title>
        <v-card-text class="d-flex flex-column align-center pa-8">
          <v-icon size="48" color="green" class="mb-4">
            mdi-check-circle-outline
          </v-icon>
          <span style="opacity: 0.9 !important; color: black; font-size: 16">
            Ya cobraste este servicio, ahora sólo debés facturarlo.
          </span>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="succesCancelDialog" max-width="542" persistent>
      <v-card>
        <v-card-title class="pt-6 px-6 pb-0 d-flex align-start">
          <v-icon size="48" class="mr-4 revision-icon" color="green"
            >mdi-check-circle-outline</v-icon
          >
          <span class="font-weight-bold hidden-sm-and-down"
            >Revisión solicitada</span
          >
          <v-spacer></v-spacer>
          <v-icon class="revision-icon" @click="succesCancelDialog = false"
            >clear</v-icon
          >
          <span
            class="font-weight-bold hidden-sm-and-up pt-4 pb-2"
            style="width: 100%"
            >Revisión solicitada</span
          >
        </v-card-title>
        <v-card-text class="revision-text-container">
          <span style="opacity: 0.9" class=""
            >¡Ya recibimos tu solicitud! Nuestro equipo revisará la información
            y te contactará a la brevedad</span
          >
        </v-card-text>
        <v-card-actions class="pa-6 pt-8">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="blue darken-2 white--text wid"
            @click="succesCancelDialog = false"
            >Aceptar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="errorCancelDialog" max-width="542" persistent>
      <v-card>
        <v-card-title class="pt-6 px-6 pb-0 d-flex align-start">
          <v-icon size="48" class="mr-4 revision-icon" color="red accent-2"
            >mdi-close-circle-outline</v-icon
          >
          <span class="hidden-sm-and-down">No se pudo enviar la solicitud</span>
          <v-spacer></v-spacer>
          <v-icon class="revision-icon" @click="errorCancelDialog = false"
            >clear</v-icon
          >
          <span
            class="font-weight-bold hidden-sm-and-up pt-4 pb-2"
            style="width: 100%"
            >No se pudo enviar la solicitud</span
          >
        </v-card-title>
        <v-card-text class="revision-text-container">
          <span style="opacity: 0.9">
            Ocurrió un error y no pudimos enviar la solicitud de revisión, volvé
            a intentarlo en unos minutos.
          </span>
        </v-card-text>
        <v-card-actions class="pa-6 pt-8">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="blue darken-2 white--text wid"
            @click="errorCancelDialog = false"
            >Aceptar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="revisionDetailDialog" max-width="641" persistent>
      <v-card>
        <v-card-title class="pt-6 px-6 pb-0 d-flex align-start">
          <v-icon size="48" class="mr-4 revision-icon" color="blue darken-2"
            >mdi-information-outline</v-icon
          >
          <span class="font-weight-bold hidden-sm-and-down"
            >Revisión solicitada</span
          >
          <v-spacer></v-spacer>
          <v-icon class="revision-icon" @click="revisionDetailDialog = false"
            >clear</v-icon
          >
          <span
            class="font-weight-bold hidden-sm-and-up pt-4 pb-2"
            style="width: 100%"
            >Revisión solicitada</span
          >
        </v-card-title>
        <v-card-text class="revision-text-container">
          <span style="opacity: 0.9">
            ¡Ya recibimos tu solicitud! Nuestro equipo revisará la información y
            te contactaremos a la brevedad.
          </span>
          <div class="d-flex flex-column">
            <span class="font-weight-medium mb-2"> Datos registrados: </span>
            <ul v-for="(item, index) in requestRevisionDetails" :key="index">
              <li>
                <span class="font-weight-bold">{{ item.label }}:</span>
                {{ item.text }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6 pt-8">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="blue darken-2 white--text"
            class="px-5 font-weight-bold wid"
            large
            @click="revisionDetailDialog = false"
            >Aceptar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import moment from "moment";
import SkeletonForm from "@/components/SkeletonForm.vue";
import { RESP_INSCRIPTO } from "@/helpers/constants";

const CURRENCY_FORMATTER = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
});

const MAX_TRANSACTIONS = 15;
const PENDING_CONCILIATION = 7;

export default {
  name: "InvoiceSelectionStep",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    v: {
      type: Object,
      required: true,
    },
  },
  components: { SkeletonForm },
  data: () => ({
    RESP_INSCRIPTO,
    MAX_TRANSACTIONS,
    PENDING_CONCILIATION,
    selectAll: false,
    warningAlert: false,
    selectedTransaction: null,
    revisionDialog: false,
    revisionDetailDialog: false,
    successDialog: false,
    succesCancelDialog: false,
    errorCancelDialog: false,
    transactionToRevision: null,
    requestRevisionIsLoading: false,
    formValid: false,
    rules: {
      required: (v) => !!v || "Este campo es obligatorio",
      max500: (v) => (v && v.length <= 500) || "Máximo 500 caracteres",
      numeric: (v) => /^\d+(\.\d{1,2})?$/.test(v) || "Solo se permiten números",
    },
    revisionForm: {
      reasonReview: "",
      suggestedAmount: null,
    },
  }),
  computed: {
    ...mapState("invoice", ["pendingTransactions", "isLoading"]),

    ...mapGetters("invoice", ["ivaCondition"]),

    transactionErrors() {
      const errors = [];
      if (!this.v.transactions.$error) return errors;
      !this.v.transactions.required && errors.push("Este campo es obligatorio");
      !this.v.transactions.minLength &&
        errors.push("Debe seleccionar al menos una transacción");
      !this.v.transactions.maxLength &&
        errors.push("No puede seleccionar más de 15 transacciones");
      return errors;
    },
    allTransactionSelected() {
      const filteredTransactions = this.pendingTransactions.filter(
        (transaction) => transaction.status !== PENDING_CONCILIATION
      );
      return (
        this.formData.transactions.length === MAX_TRANSACTIONS ||
        this.formData.transactions.length === filteredTransactions.length
      );
    },
    totalAmountAll() {
      return this.formData.transactions.reduce(
        (total, transaction) => total + Number(transaction.amount),
        0
      );
    },
    requestDetails() {
      const t = this.transactionToRevision.request;
      if (!t) return [];

      const formatCurrency = (value) => {
        if (value === null || value === undefined) return "Sin datos";
        return `$${value.toLocaleString("es-AR", {
          minimumFractionDigits: 2,
        })}`;
      };

      // const movida = formatCurrency(t?.budget?.precio_movida ?? 0);
      // const km = formatCurrency(t?.budget?.kilometros_recorridos ?? 0);
      const total = formatCurrency(t?.budget?.total);
      const fecha = t.created_time
        ? `${moment(t.created_time).format("DD/MM/YYYY")} | ${moment(
            t.created_time
          ).format("HH:mm")} hs`
        : "Sin datos";

      const items = [
        { icon: "📅", label: "Asignación", text: fecha },
        {
          icon: "🔧",
          label: "Tipo de servicio",
          text: t.service || "Sin datos",
        },
        { icon: "🚗", label: "Vehículo", text: t.vehicle || "Sin datos" },
      ];

      if (t.sub_scheme === "Mecanica ligera") {
        items.push({
          icon: "📍",
          label: "Ubicación",
          text: t.origin || "Sin datos",
        });
      } else {
        items.push(
          { icon: "📍", label: "Origen", text: t.origin || "Sin datos" },
          { icon: "📍", label: "Destino", text: t.destination || "Sin datos" }
        );
      }

      const presupuestoParts = [];

      // presupuestoParts.push(`Movida ${movida}`);
      // presupuestoParts.push(`Km ${km} (${t.budget?.kilometros_recorridos ?? 0} km)`);
      if (Array.isArray(t?.budget?.items)) {
        t.budget.items.forEach((it) => {
          const descripcion = it.descripcion ? it.descripcion : "Sin datos";
          const monto =
            it.monto !== null && it.monto !== undefined
              ? formatCurrency(it.monto)
              : "Sin datos";
          if (descripcion !== "Sin datos" || monto !== "Sin datos") {
            presupuestoParts.push(`${descripcion}: ${monto}`);
          }
        });
      }

      items.push(
        {
          icon: "📝",
          label: "Presupuesto",
          text: presupuestoParts.length
            ? presupuestoParts.join(" / ")
            : "Sin datos",
        },
        { icon: "💲", label: "TOTAL", text: total }
      );

      return items;
    },

    requestRevisionDetails() {
      const t = this.transactionToRevision?.requested_review;
      if (!t) return [];

      const formatCurrency = (value) => {
        const num = Number(value);
        if (isNaN(num)) return "Sin datos";
        return num.toLocaleString("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        });
      };

      const monto = formatCurrency(t?.suggested_amount);
      return [
        {
          label: "Motivo de revisión",
          text: t.reason_review || "Sin datos",
        },
        { label: "Monto sugerido", text: monto || "Sin datos" },
      ];
    },
    totalAmountWithoutPaid() {
      return this.formData.transactions.reduce((total, transaction) => {
        if (!transaction.paid) {
          return total + Number(transaction.amount);
        }
        return total;
      }, 0);
    },
    transactionLimitReached() {
      return this.formData.transactions.length === MAX_TRANSACTIONS;
    },
  },
  async created() {
    await this.getPendingTransactions();
  },
  methods: {
    ...mapActions("invoice", ["getPendingTransactions", "requestRevision"]),

    formatCurrency(amount = 0) {
      return CURRENCY_FORMATTER.format(amount);
    },
    toggleSelectedAll(selected) {
      if (selected) {
        const filtered = this.pendingTransactions.filter(
          (t) => !t.blocked_by_issue
        );
        for (const transaction of filtered) {
          if (this.formData.transactions.length === MAX_TRANSACTIONS) break;

          if (transaction.status === PENDING_CONCILIATION) continue;

          if (
            !this.formData.transactions.some((t) => t.id === transaction.id)
          ) {
            this.formData.transactions.push(transaction);
          }
        }
      } else {
        this.formData.transactions = this.formData.transactions.filter(
          (transaction) =>
            !this.pendingTransactions.some((t) => t.id === transaction.id)
        );
      }
    },
    disableCheckbox(transaction) {
      const isSelected = this.formData.transactions.some(
        (t) => t.id === transaction.id
      );
      return (
        transaction.status === PENDING_CONCILIATION ||
        (this.formData.transactions.length === MAX_TRANSACTIONS && !isSelected)
      );
    },
    cancelRevision() {
      this.revisionDialog = false;
      this.revisionForm.reasonReview = null;
      this.revisionForm.suggestedAmount = null;
      this.$refs.revisionFormRef.reset();
    },
    showAlert(transaction) {
      this.selectedTransaction = transaction;
      this.warningAlert = true;
    },
    closeAlert() {
      this.warningAlert = false;
      this.selectedTransaction = null;
    },
    openRevisionDialog(transaction) {
      this.transactionToRevision = transaction;
      this.revisionDialog = true;
    },
    async submitRevisionForm() {
      const isValid = this.$refs.revisionFormRef.validate();
      this.requestRevisionIsLoading = true;
      if (isValid) {
        try {
          await this.requestRevision({
            transactionId: this.transactionToRevision.id,
            motivo: this.revisionForm.reasonReview,
            montoSugerido: this.revisionForm.suggestedAmount,
          });

          await this.getPendingTransactions();
          this.$refs.revisionFormRef.reset();
          this.revisionDialog = false;
          this.succesCancelDialog = true;
        } catch (error) {
          console.error(error);
          this.revisionDialog = false;
          this.errorCancelDialog = true;
        } finally {
          this.requestRevisionIsLoading = false;
        }
      }
    },
    openRevisionDetailDialog(transaction) {
      this.transactionToRevision = transaction;
      this.revisionDetailDialog = true;
    },
    openSuccesDialog() {
      this.successDialog = true;
    },
    showRequest(requestId) {
      this.$router.push({
        name: "request-detail",
        params: { id: requestId },
      });
    },
    showAlert(transaction) {
      this.selectedTransaction = transaction;
      this.warningAlert = true;
    },
    closeAlert() {
      this.warningAlert = false;
      this.selectedTransaction = null;
    },
    transactionRequestHasGruaSubScheme(transaction) {
      if (!transaction || !transaction.request) return false;

      return (
        transaction.request?.sub_scheme === "Gruas" ||
        transaction.request?.sub_scheme === "Mecanica ligera"
      );
    },
  },
  watch: {
    allTransactionSelected: {
      handler(newValue) {
        if (newValue) {
          this.selectAll = true;
        } else {
          this.selectAll = false;
        }
      },
      immediate: true,
    },
  },
};
</script>
<style scoped>
.revision-text-container {
  opacity: 0.9;
  color: black !important;
  padding: 0 85px !important;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.request-information {
  font-size: 16px;
}
::v-deep .wid {
  width: auto;
}

@media (max-width: 600px) {
  ::v-deep .wid {
    width: 100% !important;
  }
  .revision-text-container {
    padding: 0 24px !important;
  }

  .revision-icon {
    font-size: 24px !important;
  }

  .request-information {
    font-size: 14px;
  }
}

@media (max-width: 959px) {
  .short-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.v-list--dense .v-list-item {
  min-height: 0px;
}
.custom-prefix >>> .v-text-field__prefix {
  opacity: 0.6;
}
.form-gap {
  gap: 16px;
}
</style>
